/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import type { SubmitEvent } from "react"
import { useRef, useState, useTransition } from "react"

import { getShortcutLabels, useKeyboardShortcut } from "../../../utils/use-keyboard-shortcut"

import "./application-search.css"

const FOCUS_SHORTCUT = "$mod+k"

interface Props {
    onSearch: (query: string) => Promise<void>
}

export const ApplicationSearch = ({ onSearch }: Props) => {
    const [query, setQuery] = useState("")
    const [isPending, startTransition] = useTransition()

    const inputRef = useRef<HTMLInputElement>(null)
    const trimmedQuery = query.trim()

    useKeyboardShortcut(FOCUS_SHORTCUT, () => inputRef.current?.focus())

    const submit = (e: SubmitEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (isPending || !trimmedQuery) return

        startTransition(async () => {
            await onSearch(trimmedQuery)
        })
    }

    return (
        <form className={clsx("application-search", { "is-busy": isPending })} role="search" onSubmit={submit}>
            <span className="search-start">
                <img src="/assets/ic-search.svg" alt="" />
                <span className="spinner-border spinner-border-sm text-legacy-secondary" />
            </span>
            <input
                ref={inputRef}
                type="text"
                placeholder="Enter Client ID or Device UUID"
                className="search-input form-control form-control-sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />
            <span className="search-end">
                <button
                    type="submit"
                    className="search-button btn btn-light"
                    aria-label="Search"
                    disabled={isPending || !trimmedQuery}
                >
                    <img src="/assets/ic-arrow-right-short.svg" alt="" />
                </button>
            </span>
            <div className="search-hint">
                {getShortcutLabels(FOCUS_SHORTCUT).map((label) => (
                    <kbd key={label}>{label}</kbd>
                ))}
            </div>
        </form>
    )
}
